import { Component, ElementRef, EventEmitter, Inject, Input, OnChanges, OnInit, Optional, Output, SimpleChanges, ViewChild } from '@angular/core';
import { Location } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { DocumentPdfViewerComponent } from '../document-pdf-viewer/document-pdf-viewer.component';
import {
  DwExpressionService, DwFormContextMode, DwMdFormContextInfo, DwMetaDataFormApi, DwMetaDataFormStateService,
  DwMetaDataService, DwMetaDataServiceToken, DwSectionBaseComponent, DwUiConfigRegistryService, DwUIMetaDataConfig, DwUIMetaDataConfigToken,
  DwLookupSearchType, DwOrmDataService, DwOrmDataServiceToken, DwQueryHelper, DwQueryFilter, DwOperator, DwFilterType,
  isAfterDate,
  DwMediaRepositoryService,
  DwLoggingService
}
  from '@devwareapps/devware-cap';
import { DocumentItemEntity, DocumentTypeAllItems } from '../../../../meta-data/app-meta-data.service';
import { StructuredDocumentHierarchyModel, StructuredDocumentViewerModel } from '../../structured-content/models/structured-document-viewer.model';
import { StructuredDocumentViewerService } from '../../structured-content/services/structured-document-viewer.service';
import { catchError } from 'rxjs/operators';
import { forkJoin, of } from 'rxjs';
import { finalize } from 'rxjs/operators';

@Component({
  selector: 'app-document-reference-viewer',
  templateUrl: './document-reference-viewer.component.html',
  styleUrls: ['./document-reference-viewer.component.scss']
})
export class DocumentReferenceViewerComponent extends DwSectionBaseComponent implements OnInit, OnChanges {
  @Input() documentItem: DocumentItemEntity;
  @Input() hideHeader: boolean = false;
  @Input() initialPageNumber?: number;
  @Input() pageNumbers?: string;
  @Input() documentVersionId?: number;
  @Input() useStructuredPartChildDropdownNavigation: boolean = true;

  // When a host listens for (close), the close / back button delegates
  // to the host instead of running `location.back()`. Lets the viewer
  // be embedded (e.g. in the lesson study companion) without yanking
  // the user out of the surrounding page.
  @Output() close = new EventEmitter<void>();

  @ViewChild(DocumentPdfViewerComponent) pdfViewer?: DocumentPdfViewerComponent;
  @ViewChild('searchInputRef') searchInputRef?: ElementRef<HTMLInputElement>;

  documentUrl: string = '';
  formApi: DwMetaDataFormApi;
  isFullscreen: boolean;
  currentPage: number = 0;
  totalPages: number = 0;

  searchOpen: boolean = false;
  searchQuery: string = '';
  matchCount: { current: number; total: number } = { current: 0, total: 0 };

  currentScale: number = 1;

  get zoomPercent(): number {
    return Math.round(this.currentScale * 100);
  }

  highlightModeOn: boolean = false;

  kebabOpen: boolean = false;

  get progressPercent(): number {
    return this.totalPages > 0 ? (this.currentPage / this.totalPages) * 100 : 0;
  }

  onPageChange(page: number): void {
    this.currentPage = page;
  }

  onPagesLoaded(total: number): void {
    this.totalPages = total;
  }

  onSearchMatchCount(count: { current: number; total: number }): void {
    this.matchCount = count;
  }

  onScaleChange(scale: number): void {
    this.currentScale = scale;
  }

  toggleHighlightMode(): void {
    this.highlightModeOn = !this.highlightModeOn;
    this.pdfViewer?.setHighlightMode(this.highlightModeOn);
  }

  toggleKebab(): void {
    this.kebabOpen = !this.kebabOpen;
  }

  closeKebab(): void {
    this.kebabOpen = false;
  }

  toggleSearch(): void {
    this.searchOpen = !this.searchOpen;
    if (this.searchOpen) {
      // focus the input on next tick so the *ngIf has materialized the element
      setTimeout(() => this.searchInputRef?.nativeElement.focus(), 0);
    } else {
      this.clearSearch();
    }
  }

  onSearchInput(query: string): void {
    this.searchQuery = query;
    this.pdfViewer?.searchInDocument(query);
  }

  findPrevious(): void {
    if (!this.searchQuery) return;
    this.pdfViewer?.searchInDocument(this.searchQuery, { findPrevious: true });
  }

  findNext(): void {
    if (!this.searchQuery) return;
    this.pdfViewer?.searchInDocument(this.searchQuery, { findPrevious: false });
  }

  closeSearch(): void {
    this.searchOpen = false;
    this.clearSearch();
  }

  private clearSearch(): void {
    this.searchQuery = '';
    this.matchCount = { current: 0, total: 0 };
    // dispatch an empty find to clear pdfjs's highlight state
    this.pdfViewer?.searchInDocument('', { highlightAll: false });
  }


  constructor(
    dwExpressionService: DwExpressionService,
    protected dwUiConfigRegistryService: DwUiConfigRegistryService,
    @Inject(DwUIMetaDataConfigToken) uiMetaDataConfig: DwUIMetaDataConfig,
    @Inject(DwMetaDataServiceToken) dwMetaDataService: DwMetaDataService,
    @Inject(DwOrmDataServiceToken) private dataService: DwOrmDataService,
    @Optional()private dwFormStateService: DwMetaDataFormStateService,
    private dwMediaRepositoryService: DwMediaRepositoryService,
    private dwLoggingService: DwLoggingService,
    private structuredDocumentViewerService: StructuredDocumentViewerService,
    private location: Location,
    private activatedRoute: ActivatedRoute
  ) {
    super(dwExpressionService, dwUiConfigRegistryService, uiMetaDataConfig, dwMetaDataService);
    this.formApi = this.dwFormStateService?.state?.formApi;
  }

  ngOnInit(): void {
    this.refreshPageNumberChips();
    this.setupDocumentUrl();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes.documentItem && !changes.initialPageNumber && !changes.pageNumbers) {
      return;
    }

    if (changes.pageNumbers) {
      this.refreshPageNumberChips();
    }

    this.resetViewerState();
    this.setupDocumentUrl();
  }

  setupComponentComplete(): void {
    this.runAfterFormDataLoaded(() => {
      this.refreshPageNumberChips();
      this.setupDocumentUrl();
    });
  }

  
  toggleFullscreen() {
    this.isFullscreen = !this.isFullscreen;
  }

  goBack() {
    // EventEmitter.observed isn't available on Angular 10 (RxJS 6).
    // observers.length is the equivalent and works back to Angular 2.
    if (this.close.observers.length > 0) {
      this.close.emit();
      return;
    }
    this.location.back();
  }

  showPDFViewer: boolean;
  showEmbeddedViewer: boolean;
  showStructuredViewer: boolean;
  structuredViewerData: StructuredDocumentViewerModel | null = null;
  structuredHierarchyData: StructuredDocumentHierarchyModel | null = null;
  structuredViewerError: string | null = null;
  isStructuredViewerLoading: boolean = false;
  pageNumberChips: DocumentPageJumpChip[] = [];
  private lastLoadedDocumentItemId?: number;

  get resolvedInitialPageNumber(): number | undefined {
    if (this.initialPageNumber && this.initialPageNumber > 0) {
      return this.initialPageNumber;
    }

    const routePageValue = this.activatedRoute?.snapshot?.queryParamMap?.get('page');
    const parsedPageValue = routePageValue ? parseInt(routePageValue, 10) : null;

    return parsedPageValue && parsedPageValue > 0 ? parsedPageValue : undefined;
  }

  jumpToPageRange(pageNumber: number): void {
    this.pdfViewer?.goToPage(pageNumber);
  }

  trackByPageJumpChip(index: number, chip: DocumentPageJumpChip): number {
    return chip.firstPageNumber;
  }
  
  
  private setupDocumentUrl() {

    if (!this.documentItem && this.formApi) {
      this.documentItem = this.formApi.getFormData<DocumentItemEntity>();
    }

    if (!this.documentItem?.DocumentItemId) {
      console.warn('DocumentReferenceViewerComponent: No DocumentItem available to display.');
      return;
    }

    if (this.lastLoadedDocumentItemId === this.documentItem.DocumentItemId
      && (this.isStructuredViewerLoading || !!this.structuredViewerData || !!this.documentUrl)) {
      return;
    }

    this.lastLoadedDocumentItemId = this.documentItem.DocumentItemId;

    this.showPDFViewer = this.documentItem?.DocumentTypeId === DocumentTypeAllItems.PDFDocument;
    this.showEmbeddedViewer = this.documentItem?.DocumentTypeId === DocumentTypeAllItems.SketchFab3D;
    this.showStructuredViewer = this.documentItem?.DocumentTypeId === DocumentTypeAllItems.FARAIM;

    if (this.showStructuredViewer) {
      this.isStructuredViewerLoading = true;
      const hierarchyRequest = this.useStructuredPartChildDropdownNavigation
        ? this.structuredDocumentViewerService.getStructuredDocumentHierarchy(this.documentItem.DocumentItemId, this.documentVersionId)
            .pipe(catchError(() => of(null)))
        : of(null);

      forkJoin({
        structuredViewer: this.structuredDocumentViewerService.getStructuredDocumentViewer(this.documentItem.DocumentItemId, this.documentVersionId)
          .pipe(catchError(error => {
            this.structuredViewerError = this.getStructuredViewerErrorMessage(error);
            return of(null);
          })),
        structuredHierarchy: hierarchyRequest
      })
        .pipe(finalize(() => {
          this.isStructuredViewerLoading = false;
        }))
        .subscribe(result => {
          if (result.structuredViewer?.DocumentVersionId) {
            this.structuredViewerData = result.structuredViewer;
            this.structuredHierarchyData = result.structuredHierarchy?.DocumentVersionId ? result.structuredHierarchy : null;
            this.structuredViewerError = null;
            return;
          }

          if (!this.structuredViewerError) {
            this.structuredViewerError = 'Structured document content is not available for this FAR/AIM document yet.';
          }
        });

      return;
    }

    if (this.showPDFViewer && this.documentItem?.MediaId) {
      this.dwMediaRepositoryService.loadMedia(this.documentItem.MediaId)
        .subscribe(media => {
          if (this.showPDFViewer) {
           //this.dwLoggingService.logVerbose(`DocumentReferenceViewerComponent: Viewing PDF "${this.documentItem.DocumentItemName}" (${this.documentItem.DocumentItemId}) - MediaGuid ${media[0].FileName}`, null, true);
          }

          this.documentUrl = this.dwMediaRepositoryService.getImageUrlFromMediaPath(media[0].MediaPath);
        });
    }
  }

  private resetViewerState(): void {
    this.documentUrl = '';
    this.showPDFViewer = false;
    this.showEmbeddedViewer = false;
    this.showStructuredViewer = false;
    this.structuredViewerData = null;
    this.structuredHierarchyData = null;
    this.structuredViewerError = null;
    this.isStructuredViewerLoading = false;
    this.lastLoadedDocumentItemId = undefined;
    this.currentPage = 0;
    this.totalPages = 0;
    this.currentScale = 1;
    this.highlightModeOn = false;
    this.kebabOpen = false;
    this.closeSearch();
  }

  private buildPageNumberChips(pageNumbers?: string): DocumentPageJumpChip[] {
    if (!pageNumbers) {
      return [];
    }

    const uniqueSortedPages = Array.from(new Set(
      pageNumbers
        .split(',')
        .map(pageText => parseInt(pageText.trim(), 10))
        .filter(pageNumber => !isNaN(pageNumber) && pageNumber > 0)
    )).sort((a, b) => a - b);

    if (!uniqueSortedPages.length) {
      return [];
    }

    const chips: DocumentPageJumpChip[] = [];
    let rangeStart = uniqueSortedPages[0];
    let previousPage = uniqueSortedPages[0];

    for (let index = 1; index <= uniqueSortedPages.length; index++) {
      const currentPage = uniqueSortedPages[index];
      const rangeContinues = currentPage === previousPage + 1;

      if (rangeContinues) {
        previousPage = currentPage;
        continue;
      }

      chips.push({
        firstPageNumber: rangeStart,
        label: rangeStart === previousPage ? `Page ${rangeStart}` : `Pages ${rangeStart}-${previousPage}`
      });

      rangeStart = currentPage;
      previousPage = currentPage;
    }

    return chips;
  }

  private refreshPageNumberChips(): void {
    this.pageNumberChips = this.buildPageNumberChips(this.pageNumbers);
  }

  private getStructuredViewerErrorMessage(error: any): string {
    if (error?.status === 404) {
      return 'Structured document content has not been loaded for this FAR/AIM document yet.';
    }

    return 'There was a problem loading the FAR/AIM content. Please try again later.';
  }

}

interface DocumentPageJumpChip {
  firstPageNumber: number;
  label: string;
}
